import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { useLoginStore } from '../../../../store/useLogin';
import { useFranchiseStore } from '../../../../store/useFranchise';

import DatePickerInput from '../../../form/DatePickerInput';
import { formatDate } from '../../../../util/util';
import BokbiDepositModal from '../modal/BokbiDepositModal';
import '../modal/BokbiDepositModal.css';
import { toast } from 'react-toastify';
import apiClient from '../../../../service/apiClient';

interface IBokbiStatisticsFilter {
  getFilteredBokbiData: (params: any) => void; // API 호출 함수
  onFranchiseChange?: (franchiseId: string) => void; // 프랜차이즈 변경 시 호출되는 콜백
}

const BokbiStatisticsFilter = ({
  getFilteredBokbiData,
  onFranchiseChange,
}: IBokbiStatisticsFilter) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { role } = useLoginStore();
  const { franchises } = useFranchiseStore();

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [selectedFranchiseId, setSelectedFranchiseId] = useState<string>('');
  const [showFranchiseDropdown, setShowFranchiseDropdown] = useState<boolean>(false);
  const [franchiseSearchText, setFranchiseSearchText] = useState<string>('');

  const [franchise, setFranchise] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const currentQuery = queryString.parse(location.search);
    if (currentQuery.startDt) {
      return new Date(currentQuery.startDt as string);
    }
    const today = new Date();
    return new Date(today.setDate(today.getDate() - 31));
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const currentQuery = queryString.parse(location.search);
    if (currentQuery.endDt) {
      return new Date(currentQuery.endDt as string);
    }
    return new Date();
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const currentQuery = queryString.parse(location.search);
    // 쿼리 스트링 값에 따라 필드 업데이트
    if (currentQuery.franchiseId) {
      setFranchise(currentQuery.franchiseId as string);
      setSelectedFranchiseId(currentQuery.franchiseId as string);
    } else {
      setFranchise(''); // 기본값
      setSelectedFranchiseId('');
    }

    if (currentQuery.startDt) {
      setStartDate(new Date(currentQuery.startDt as string));
    } else {
      const today = new Date();
      setStartDate(new Date(today.setDate(today.getDate() - 31)));
    }
    if (currentQuery.endDt) {
      setEndDate(new Date(currentQuery.endDt as string));
    } else {
      setEndDate(new Date());
    }
  }, [location.search]);

  const updateQueryString = useCallback(
    (updatedFields: Record<string, string | undefined>) => {
      const currentQuery = queryString.parse(location.search);
      const updatedQuery = {
        ...currentQuery,
        ...updatedFields,
      };

      const newQueryString = queryString.stringify(updatedQuery);
      navigate(`?${newQueryString}`);
    },
    [location.search, navigate]
  );

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      setStartDate(date);
      const startDt = date ? formatDate(date)['yyyy-mm-ddTHH:mm:ss'] : undefined;
      updateQueryString({ startDt });
    },
    [updateQueryString]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      setEndDate(date);
      const endDt = date ? formatDate(date)['yyyy-mm-ddTHH:mm:ss'] : undefined;
      updateQueryString({ endDt });
    },
    [updateQueryString]
  );

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const params = {
        franchiseId: franchise,
        startDt: startDate ?
          formatDate(startDate)['yyyy-mm-ddTHH:mm:ss'] : undefined,
        endDt: endDate ?
          formatDate(endDate)['yyyy-mm-ddTHH:mm:ss'] : undefined,
      };

      getFilteredBokbiData(params);
    },
    [getFilteredBokbiData, franchise, startDate, endDate]
  );

  const handleDownload = useCallback(async () => {
    try {
      const startDateStr = startDate ?
        formatDate(startDate)['yyyy-mm-dd'] : undefined;
      const endDateStr = endDate ?
        formatDate(endDate)['yyyy-mm-dd'] : undefined;

      if (!startDateStr || !endDateStr) {
        toast.error('날짜 범위를 선택해주세요.');
        return;
      }

      const response = await apiClient.get('/charge-hst/download', {
        params: {
          startDate: `${startDateStr}T00:00:00`,
          endDate: `${endDateStr}T23:59:59`
        },
        responseType: 'blob'
      });

      // 파일 다운로드 처리
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `주문서_${startDateStr}_${endDateStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('주문서 다운로드가 완료되었습니다.');
    } catch (error) {
      console.error('다운로드 실패:', error);
      toast.error('해당 기간에 일치하는 데이터가 없습니다.');
    }
  }, [startDate, endDate]);

  const handleFranchiseSelect = useCallback(
    (franchiseId: string) => {
      setSelectedFranchiseId(franchiseId);
      setFranchise(franchiseId);
      setShowFranchiseDropdown(false);
      setFranchiseSearchText('');
      updateQueryString({ franchiseId });
      
      // 프랜차이즈 변경 콜백 호출
      if (onFranchiseChange) {
        onFranchiseChange(franchiseId);
      }
    },
    [updateQueryString, onFranchiseChange]
  );

  return (
    <>
      <div className="!w-full">
        <div className="!flex !justify-between !items-center">
          <button
            onClick={() => setIsFilterOpen(prev => !prev)}
            className="md:!hidden !px-3 !py-1.5 !border !rounded !text-sm"
          >
            {isFilterOpen ? '닫기' : '검색필터 열기'}
          </button>
        </div>
        <form 
          onSubmit={handleSearch} 
          className={`
            ${isFilterOpen ? '!flex' : '!hidden'}
            md:!flex !mt-4 !flex-col !gap-4 md:!flex-row md:!items-center md:!justify-between !w-full
          `}
        >
          <div className="!flex !items-center !gap-4 !flex-wrap">
            <span className="!text-lg !font-semibold !text-gray-800">복비 통계</span>
            <div className="!flex !items-center !gap-1">
              <DatePickerInput
                value={startDate}
                onChange={handleStartDateChange}
                placeholder="시작일"
                width={130}
              />
              <span className="!text-gray-400">~</span>
              <DatePickerInput
                value={endDate}
                onChange={handleEndDateChange}
                placeholder="종료일"
                width={130}
              />
            </div>
            {role === 'ROLE_ADMIN' && (
              <div className="!flex !items-center !gap-3">
                {/* 가맹점 선택 */}
                <div className="!flex !items-center !gap-2">
                  <span className="!text-sm !font-medium !text-gray-700">가맹점:</span>
                  <div className="!relative franchise-dropdown-container">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowFranchiseDropdown(!showFranchiseDropdown);
                      }}
                      className="!flex !items-center !gap-2 !px-4 !py-2 !bg-white !border !border-gray-300 !rounded-lg !text-sm !font-medium !text-gray-700 hover:!bg-gray-50 !transition-colors"
                    >
                      <span>{selectedFranchiseId ? franchises?.find(f => f.id === selectedFranchiseId)?.companyName || '회사 선택' : '회사 선택'}</span>
                      <svg className="!w-4 !h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showFranchiseDropdown && (
                      <div className="!absolute !left-0 !mt-2 !w-64 !bg-white !border !border-gray-200 !rounded-lg !shadow-lg !z-10">
                        {/* 검색 입력창 */}
                        <div className="!p-3 !border-b !border-gray-200">
                          <div className="!relative">
                            <input
                              type="text"
                              value={franchiseSearchText}
                              onChange={(e) => setFranchiseSearchText(e.target.value)}
                              placeholder="회사명으로 검색..."
                              className="!w-full !pl-8 !pr-3 !py-2 !text-sm !border !border-gray-300 !rounded-md focus:!outline-none focus:!ring-2 focus:!ring-blue-500 focus:!border-transparent"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <svg className="!absolute !left-2.5 !top-1/2 !transform !-translate-y-1/2 !w-4 !h-4 !text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                        </div>

                        <div className="!max-h-60 !overflow-y-auto">
                          {franchises && franchises.length > 0 ? (
                            franchises
                              .filter(franchise => {
                                if (!franchiseSearchText) return true;
                                return franchise.companyName && franchise.companyName.toLowerCase().includes(franchiseSearchText.toLowerCase());
                              })
                              .map((franchiseItem) => (
                                <button
                                  type="button"
                                  key={franchiseItem.id}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleFranchiseSelect(franchiseItem.id);
                                  }}
                                  className="!w-full !px-4 !py-2 !text-left !text-sm !text-gray-700 hover:!bg-gray-100 !transition-colors"
                                >
                                  <div className="!font-medium">{franchiseItem.companyName || ''}</div>
                                  <div className="!text-xs !text-gray-500">{franchiseItem.ceo || '-'} | {franchiseItem.telNo || '-'}</div>
                                </button>
                              ))
                          ) : (
                            <div className="!px-4 !py-3 !text-sm !text-gray-500">회사 목록이 없습니다.</div>
                          )}
                          {franchises && franchises.filter(franchise => {
                            if (!franchiseSearchText) return true;
                            return franchise.companyName && franchise.companyName.toLowerCase().includes(franchiseSearchText.toLowerCase());
                          }).length === 0 && franchiseSearchText && (
                            <div className="!px-4 !py-3 !text-sm !text-gray-500">검색 결과가 없습니다.</div>
                          )}
                        </div>
                        <div className="!border-t !border-gray-200 !px-4 !py-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFranchiseSelect('');
                            }}
                            className="!text-sm !text-red-600 hover:!text-red-700"
                          >
                            선택 취소
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* 검색 버튼 */}
                <button
                  type="submit"
                  className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-700 !text-white !rounded-lg !text-sm !font-medium !flex !items-center !gap-2 !transition-colors"
                >
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="!text-sm" />
                  검색
                </button>
                
                {/* 계산서 다운로드 버튼 */}
                <button
                  type="button"
                  onClick={handleDownload}
                  className="!px-4 !py-2 !bg-green-500 !text-white !rounded-lg hover:!bg-green-600 !transition-colors !text-sm !font-medium"
                >
                계산서 다운로드
                </button>
              </div>
            )}
            {role !== 'ROLE_ADMIN' && (
              <button
                type="submit"
                className="!px-4 !py-2 !bg-blue-400 hover:!bg-blue-500 !text-white !rounded-lg !flex !items-center !gap-2 !transition-colors !text-sm !font-medium"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} className="!text-sm" />
                검색
              </button>
            )}
          </div>
          
          {/* 복비 입금 신청 버튼 */}
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-700 !text-white !rounded-lg !text-sm !font-medium self-start md:self-center"
          >
            복비 입금 신청
          </button>
        </form>
      </div>
      
      <BokbiDepositModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default BokbiStatisticsFilter;